import React, { useRef, useState, useCallback, useMemo, useContext } from 'react';
import { Alert, Button, Icon, message } from 'antd';

import {
  ScrollableContainer,
  IconWrapper,
  JobCardsLayout,
  JobCard,
  JobTitle,
  JobCardContent,
  JobCount,
  PrinterAndTickIcon,
  JobCardContentText,
  NoApproverContent,
} from './Styles';

import { HandShakeIcon, PrinterSVG, TickSVG, SMSIcon, WarningIcon } from '../../../images/svgIcons';
import Steps from './Component/Steps';
import StatusMetrics from './StatusMetrics';
import ActionButton from '../../../components/ActionButton';
import { getZigZagPosition } from '../../../utils/javascript';
import { ArcherContainer, ArcherElement } from 'react-archer';
import { STEPS, ACTION_CARD as trans } from './TranslationValues';
import { withTranslation } from 'react-i18next';
import { ENTERPRISE_ENV, getChannelTotals, doesBatchHaveDocuments, isShortMessageEnabled, getChannelUnknownCount } from '../../../utils/helper';
import { ApprovalModal, JOBQueueModal, JobProcessingModal, PrintingModal, PrintingQueue } from '../../../utils/complexDocumentConstants';
import JobDownload from './JobDownload';
import { afterApprovedStatus, getJobStatus } from '../../../utils/constants/statusMonitoring';
import { useSessionContext } from '../../../context/sessionContext';
import { handleApprovalProcess } from '../../../utils/services/statusMonitoringAPI';
import { AppStoreContext } from '../../../context/appStoreContext';

const NAMESPACES = ['STEPS', 'ACTION_CARD'];

const steps = t => [
  {
    id: 1,
    label: t(STEPS.LEVEL_LABEL.key, STEPS.LEVEL_LABEL.value, { level: 1 }),
    status: 'completed',
  },
  {
    id: 2,
    label: t(STEPS.LEVEL_LABEL.key, STEPS.LEVEL_LABEL.value, { level: 2 }),
    status: 'pending',
  },
  {
    id: 3,
    label: t(STEPS.LEVEL_LABEL.key, STEPS.LEVEL_LABEL.value, { level: 3 }),
    status: 'failed',
  },
  {
    id: 4,
    label: t(STEPS.LEVEL_LABEL.key, STEPS.LEVEL_LABEL.value, { level: 4 }),
    status: null,
  },
];

const info = t => [
  {
    id: 1,
    label: t(trans.DOCUMENT_LABEL.key, trans.DOCUMENT_LABEL.value),
    icon: <Icon type="file-pdf" />,
    count: 0,
  },
  {
    id: 2,
    label: t(trans.EMAIL_LABEL.key, trans.EMAIL_LABEL.value),
    icon: <Icon type="mail" />,
    count: 0,
  },
  {
    id: 3,
    label: t(trans.MESSAGES_LABEL.key, trans.MESSAGES_LABEL.value),
    icon: <SMSIcon width={18} height={18} />,
    count: 0,
  },
  {
    id: 4,
    label: t(trans.PROCESS_COMPLETE_LABEL.key, trans.PROCESS_COMPLETE_LABEL.value),
    status: 'completed', // 'pending' | 'failed'
  },
  {
    id: 5,
    label: t(trans.LETTERS_LABEL.key, trans.LETTERS_LABEL.value),
    icon: <Icon type="mail" />,
    count: 0,
  }
];

const info_2 = (t, activeItem) => [
  {
    id: 1,
    label: t(trans.DOCUMENT_LABEL.key, trans.DOCUMENT_LABEL.value),
    icon: <Icon type="file-pdf" />,
    count: activeItem?.printCompleteCount || 0,
    total: activeItem?.batchStats?.filter(stat => stat.channel === 'print').reduce((acc, stat) => acc + parseInt(stat?.totalCount), 0) || 0,
  },
  {
    id: 2,
    label: t(trans.EMAIL_LABEL.key, trans.EMAIL_LABEL.value),
    icon: <Icon type="mail" />,
    count: activeItem?.batchStats?.filter(stat => stat.channel === 'email').reduce((acc, stat) => acc + parseInt(stat?.successCount), 0) || 0,
    total: activeItem?.batchStats?.filter(stat => stat.channel === 'email').reduce((acc, stat) => acc + parseInt(stat?.totalCount), 0) || 0,
  },
  {
    id: 3,
    label: t(trans.MESSAGES_LABEL.key, trans.MESSAGES_LABEL.value),
    icon: <SMSIcon width={18} height={18} />,
    count: 500,
    total: 500,
  },
];

const updateInfoWithData = (activeItem, t) => {
  const isHybrid = activeItem?.batchType === "HYBRID";
  const totals = getChannelTotals(activeItem?.batchStats || [], false, t, !isHybrid);

  const updatedInfo = info(t).reduce((acc, item) => {
  if (isHybrid) {
    if (item.id === 5) {
      acc.push({ ...item, count: totals.hybrid });
    }
    return acc;
  }

  if (item.id === 1) {
    acc.push({ ...item, count: totals.print });
  } else if (item.id === 2) {
    acc.push({ ...item, count: totals.email });
  } else if (item.id === 3 && ENTERPRISE_ENV) {
    acc.push({ ...item, count: totals.message });
  } else if (item.id === 4 && activeItem.processingComplete) {
    acc.push({ ...item, status: "success" });
  }

  return acc;
}, []);

  return updatedInfo;
};

const getApprovalMetrics = (activeItem, t) => {
  if (activeItem?.approvalComplete) {
    return [
      {
        id: 1,
        label: t(
          trans.APPROVAL_COMPLETE_LABEL.key,
          trans.APPROVAL_COMPLETE_LABEL.value,
          { level: 4 },
        ),
        status: 'success',
      },
    ];
  } else {
    let level;
    let status = activeItem?.status?.startsWith('approved') ? 'success' : activeItem?.status?.startsWith('reject') ? 'failed' : undefined;
    if (status) {
      try {
        const splitStatus = activeItem?.status?.split('_');
        level = splitStatus[splitStatus.length - 1];
        level = parseInt(level);
      } catch (err) {
        console.log("Error in splitting status : ActionOverviewCard ", err);
      }

      if (level === undefined) {
        return [];
      } else {
        return [{
          id: 1,
          label: status === 'success'
            ? t(trans.APPROVAL_COMPLETE_LABEL.key, trans.APPROVAL_COMPLETE_LABEL.value, { level })
            : t(trans.APPROVAL_REJECTED_LABEL.key, trans.APPROVAL_REJECTED_LABEL.value, { level }),
          status
        }];
      }
    } else {
      return [];
    }
  }
}

const showControlCenter = (jobDetails) => {
  if (!jobDetails.processingComplete) return false;
  const channels = jobDetails.batchStats.reduce((acc, stat) => {
    if (stat.channel === 'print') {
      return { ...acc, hasPrint: true };
    } else if (stat.channel === 'email') {
      return { ...acc, hasEmail: true };
    }
    return acc;
  }, { hasPrint: false, hasEmail: false });
  const { hasPrint, hasEmail } = channels;
  let show = false;
  if (hasPrint && jobDetails.printCompleteCount) show = true;
  if (hasEmail) {
    const allEmailBatchesCompleted = jobDetails.batchStats.filter(stat => stat.channel === 'email' && parseInt(stat.inQueueCount) !== 0);
    // console.log({ allEmailBatchesCompleted });
    if (allEmailBatchesCompleted.length === 0) {
      if (hasPrint && !jobDetails.printCompleteCount){
        show = false;
      }else{
        show = true;
      }
    } else {
      show = false;
    }
  }
  // console.log({ show });
  return show;
}

const containsOnlyEmails = (jobDetails) => jobDetails.batchStats.filter(stat => stat.channel === 'email')?.length === jobDetails.batchStats.length;
const containsPrint = (jobDetails) => jobDetails.batchStats.filter(stat => stat.channel === 'print' && Number(stat.successCount) !== 0 )?.length > 0;

const ActionOverviewCard = ({ t, activeItem, setModalType, users }) => {
  const archerContainerRef = useRef(null);
  // const [currentLevel, setCurrentLevel] = useState(0);
  const [loaders, setLoaders] = useState({});

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const { licenseDetails } = useContext(AppStoreContext);
  /*useEffect(() => {
    setTimeout(() => {
      if (archerContainerRef.current) {
        archerContainerRef.current.refreshScreen();
      }
    }, 1000);
  }, []);*/
  const {print: unknownPrintCount, email: unknownEmailCount} = getChannelUnknownCount(activeItem?.batchStats || []);
  const hasUnknown = unknownPrintCount>0 || unknownEmailCount >0;
  const batchStats = activeItem?.batchStats || [];
  const jobQueueCount = useMemo(() => {
    const { print: totalPrint = 0, email: totalEmail = 0, message: totalMessage = 0 } = getChannelTotals(batchStats, false, t, false);
    const { print: processedPrint = 0, email: processedEmail = 0, message: processedMessage = 0 } = getChannelTotals(batchStats, false, t, true);
  
    return (Number(totalPrint) + Number(totalEmail) + Number(totalMessage)) - 
           (Number(processedPrint) + Number(processedEmail) + Number(processedMessage)) - unknownPrintCount - unknownEmailCount;
  }, [batchStats]);

  const handleScroll = useCallback(() => {
    if (archerContainerRef.current) {
      archerContainerRef.current.refreshScreen();
    }
  }, []);

  const handleApproval = useCallback(async (proceed, e) => {
    e.stopPropagation();
    setLoaders(prev => ({ ...prev, ...(proceed ? { proceed: true } : { reInitiate: true }) }));
    try {      
      const response = await handleApprovalProcess({ jobId: activeItem?.id, proceedWithoutApproval: proceed });
      if(response?.statusCode === 3123) {
        message.warning(t(trans.APPROVERS_NOT_AVAILABLE_ERROR.key, trans.APPROVERS_NOT_AVAILABLE_ERROR.value));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoaders({
        proceed: false,
        reInitiate: false,
      });
    }
  }, [activeItem]);

  return (
    <ScrollableContainer>
    <ArcherContainer ref={archerContainerRef} strokeColor="#000" strokeWidth={1}>
      <JobCardsLayout onScroll={() => handleScroll()}>
        <ArcherElement
          id="element-1"
          relations={[
            {
              targetId: 'element-2',
              targetAnchor: 'left',
              sourceAnchor: 'right',
              className: 'arrows arrow-1',
            },
            !containsOnlyEmails(activeItem) && {
              targetId: 'element-6',
              targetAnchor: 'top',
              sourceAnchor: 'bottom',
              style: {},
              className: 'arrows arrow-6',
            },
          ]}
        >
          <JobCard
            position={getZigZagPosition(1, 3)}
            index={0}
            onClick={() => setModalType(JOBQueueModal)}
            width={activeItem?.status === 'no_approver_found' && 220}
          >
            <JobTitle>{t(trans.JOB_QUEUE_TITLE.key, trans.JOB_QUEUE_TITLE.value)}</JobTitle>
            <JobCardContent>
              <JobCount>{jobQueueCount}</JobCount>
              {activeItem?.status === 'no_approver_found' && (
                <NoApproverContent>
                  <Alert message={t(trans.NO_APPROVER_FOUND_WARNING.key, trans.NO_APPROVER_FOUND_WARNING.value)} type="warning" showIcon />
                  <p className="description">
                    {t(trans.NO_APPROVER_FOUND_INFO.key, trans.NO_APPROVER_FOUND_INFO.value)}
                  </p>
                  <div className="buttons">
                    <Button
                      disabled={loaders.proceed}
                      loading={loaders.reInitiate}
                      onClick={(e) => handleApproval(false, e)}
                    >
                      {t(trans.PROCEED_WITH_APPROVAL_BUTTON.key, trans.PROCEED_WITH_APPROVAL_BUTTON.value)}
                    </Button>
                    <Button 
                      className="primary"
                      disabled={loaders.reInitiate}
                      loading={loaders.proceed}
                      onClick={(e) => handleApproval(true, e)}
                    >
                      {t(trans.SKIP_APPROVAL_BUTTON.key, trans.SKIP_APPROVAL_BUTTON.value)}
                    </Button>
                  </div>
                </NoApproverContent>
              )}
            </JobCardContent>
          </JobCard>
        </ArcherElement>

        {activeItem?.status !== 'no_approver_found' && (<>
        <ArcherElement
          id="element-2"
          relations={[
            containsOnlyEmails(activeItem) && !activeItem?.requiresApproval ? {
              targetId: 'element-6',
              targetAnchor: 'left',
              sourceAnchor: 'right',
              style: {},
              className: 'arrows arrow-2',
            } : !containsOnlyEmails(activeItem) && !activeItem?.requiresApproval ? {
              targetId: 'element-4',
              targetAnchor: 'top',
              sourceAnchor: 'right',
              className: 'arrows arrow-2',
            } : {
              targetId: 'element-3',
              targetAnchor: 'left',
              sourceAnchor: 'right',
              className: 'arrows arrow-2',
            },
          ]}
        >
          <JobCard
            position={getZigZagPosition(2, 3)}
            width={
              activeItem?.requiresApproval === false &&
              activeItem.initiatedFrom === 'LIVE_API'
                ? 240
                : 220
            }
            index={1}
            onClick={() => {
              setModalType(JobProcessingModal);
            }}
          >
            <JobTitle>
              {t(trans.PROCESSING_CENTER_TITLE.key, trans.PROCESSING_CENTER_TITLE.value)}
            </JobTitle>
            <JobCardContent>
              <StatusMetrics info={updateInfoWithData(activeItem || {}, t)} isShortMessageEnabled={isShortMessageEnabled} />
              {activeItem && doesBatchHaveDocuments(activeItem) && activeItem?.processingComplete && (activeItem?.initiatedFrom === 'DRAFT_UPLOAD' || activeItem?.initiatedFrom === 'LIVE_UPLOAD')  && (
                <JobDownload activeItem={activeItem} />
              )}
              {containsPrint(activeItem) && activeItem?.requiresApproval === false && activeItem?.processingComplete &&
                activeItem.initiatedFrom === 'LIVE_API' && (
                  <ActionButton small
                     label={t(
                        trans.SEND_FOR_PRINTNG_BUTTON.key,
                        trans.SEND_FOR_PRINTNG_BUTTON.value,
                      )}
                      onClick={e => {
                          e.stopPropagation();
                          setModalType(PrintingModal);
                      }}
                    />)}
            </JobCardContent>
          </JobCard>
        </ArcherElement>
        {((activeItem?.requiresApproval && activeItem?.processingComplete && activeItem?.batchEnd) || 
        (activeItem?.requiresApproval && activeItem?.batchType === 'HYBRID')) && (
          <>
            <ArcherElement
              id="element-3"
              relations={[
                containsOnlyEmails(activeItem) ? {
                  targetId: 'element-6',
                  targetAnchor: 'top',
                  sourceAnchor: 'bottom',
                  className: 'arrows arrow-3',
                } : {
                  targetId: 'element-4',
                  targetAnchor: 'top',
                  sourceAnchor: 'bottom',
                  className: 'arrows arrow-3',
                },
              ]}
            >
              <JobCard position={getZigZagPosition(3, 3)} width={240} index={2} relative onClick={() => setModalType(ApprovalModal)}>
                <JobTitle>
                  {t(trans.APPROVAL_ENGINE_TITLE.key, trans.APPROVAL_ENGINE_TITLE.value)}
                </JobTitle>
                <JobCardContent>
                  {activeItem?.batchType === 'HYBRID' ? 
                    <div class="card-wrap">
                      <div class="card card-outlined">
                        <div class="avatar">
                          <svg viewBox="64 64 896 896" xmlns="http://www.w3.org/2000/svg">
                            <path d="M858.5 763.6a374 374 0 00-80.6-119.5 375.63 375.63 0 00-119.5-80.6c-.4-.2-.8-.3-1.2-.5C719.5 518 760 444.7 760 362c0-137-111-248-248-248S264 225 264 362c0 82.7 40.5 156 102.8 201.1-.4.2-.8.3-1.2.5-44.8 18.9-85 46-119.5 80.6a375.63 375.63 0 00-80.6 119.5A371.7 371.7 0 00136 901.8a8 8 0 008 8.2h60c4.4 0 7.9-3.5 8-7.8 2-77.2 33-149.5 87.8-204.3 56.7-56.7 132-87.9 212.2-87.9s155.5 31.2 212.2 87.9C779 752.7 810 825 812 902.2c.1 4.4 3.6 7.8 8 7.8h60a8 8 0 008-8.2c-1-47.8-10.9-94.3-29.5-138.2zM512 534c-45.9 0-89.1-17.9-121.6-50.4S340 407.9 340 362c0-45.9 17.9-89.1 50.4-121.6S466.1 190 512 190s89.1 17.9 121.6 50.4S684 316.1 684 362c0 45.9-17.9 89.1-50.4 121.6S557.9 534 512 534z"/>
                          </svg>
                        </div>
                        <div class="badge">{t(trans.APPROVER_BADGE_LABEL.key, trans.APPROVER_BADGE_LABEL.value)}</div>
                        <span class="name">{users?.find(user => user.id === parseInt(activeItem?.approver))?.displayName || ''}</span>
                      </div>
                    </div>
                  : <>
                  <Steps steps={steps(t)} status={activeItem?.status} /> {/* TODO : Show all approvals when status has been pushed to printing stage */}
                  <StatusMetrics
                    info={getApprovalMetrics(activeItem, t)}
                    isShortMessageEnabled={isShortMessageEnabled}
                  />
                  {getJobStatus(activeItem?.status, userDetails?.roles) ? <ActionButton
                    small
                    label={t(
                      trans.REVIEW_AND_APPROVE_BUTTON.key,
                      trans.REVIEW_AND_APPROVE_BUTTON.value,
                    )}
                    onClick={() => setModalType(ApprovalModal)}
                  /> : licenseDetails?.PrintConfiguration ?
                    (containsPrint(activeItem) && afterApprovedStatus.includes(activeItem?.status) && userDetails?.roles?.includes('JOBMANAGER')) && <ActionButton
                      small
                      label={t(
                        trans.SEND_FOR_PRINTNG_BUTTON.key,
                        trans.SEND_FOR_PRINTNG_BUTTON.value,
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        setModalType(PrintingModal);
                        // setCurrentLevel(level => level + 1);
                      }}
                    />
                    : <Alert message={t(trans.PRINT_SERVICE_IS_NOT_CONFIGURED.key, trans.PRINT_SERVICE_IS_NOT_CONFIGURED.value)} type="warning" showIcon style={{ fontSize: '12px' }} />
                  }
                  </>}
                </JobCardContent>
                {activeItem?.printInitiated && <IconWrapper background={'#FFF'} absolute bottom size={35}>
                  <Icon type="printer" />
                </IconWrapper>}
              </JobCard>
            </ArcherElement>
          </>
        )}
        {activeItem?.printInitiated && (
          <ArcherElement
            id="element-4"
            relations={[
              {
                targetId: 'element-5',
                targetAnchor: 'right',
                sourceAnchor: 'left',
                className: 'arrows arrow-4',
              },
            ]}
          >
            <JobCard
              position={getZigZagPosition(4, 3)}
              width={220}
              index={3}
              onClick={() => {
                setModalType('PrintingQueue');
              }}
            >
              <JobTitle>
                {t(trans.PRINTING_ENGINE_TITLE.key, trans.PRINTING_ENGINE_TITLE.value)}
              </JobTitle>
              <JobCardContent>
                <JobCardContentText>
                  <strong>{activeItem?.printQueueCount}</strong>{' '}
                  {t(trans.DOCUMENT_QUEUED_TEXT.key, trans.DOCUMENT_QUEUED_TEXT.value)}
                </JobCardContentText>
              </JobCardContent>
            </JobCard>
          </ArcherElement>
        )}
        {activeItem?.printCompleteCount ? (
            <ArcherElement
              id="element-5"
              relations={[
                {
                  targetId: 'element-6',
                  targetAnchor: 'right',
                  sourceAnchor: 'left',
                  className: 'arrows arrow-5',
                },
              ]}
            >
              <JobCard position={getZigZagPosition(5, 3)}
                    width={220}
                    index={4}
                    onClick={() => {
                      activeItem?.printCompleteCount > 0 &&
                      setModalType('PrintingCenter');
                    }}
                  >
                <JobTitle>
                  {t(trans.PRINTING_CENTER_TITLE.key, trans.PRINTING_CENTER_TITLE.value)}
                </JobTitle>
                <JobCardContent>
                  <PrinterAndTickIcon>
                    <PrinterSVG />
                    <div>
                      <IconWrapper background={'#FFE713'}>
                        <TickSVG />
                      </IconWrapper>
                    </div>
                  </PrinterAndTickIcon>
                  <JobCardContentText>
                    <strong>{activeItem?.printCompleteCount }</strong>{' '}
                    {t(trans.PRINTING_COMPLETE_TEXT.key, trans.PRINTING_COMPLETE_TEXT.value)}
                  </JobCardContentText>
                </JobCardContent>
              </JobCard>
            </ArcherElement>
        ): null}
        {(showControlCenter(activeItem) || hasUnknown) && (
            <ArcherElement id="element-6">
              <JobCard position={containsOnlyEmails(activeItem) ? getZigZagPosition(activeItem?.requiresApproval ? 4 : 3, 3) : getZigZagPosition(6, 3)} width={220} index={5} onClick={() => {
                      setModalType('ControlCenter');
                    }}>
                <JobTitle>
                  {t(trans.CONTROL_CENTER_TITLE.key, trans.CONTROL_CENTER_TITLE.value)}
                </JobTitle>
                <JobCardContent>
                {hasUnknown ? <WarningIcon /> : <HandShakeIcon /> }
                  
                  <StatusMetrics info={info_2(t, activeItem)} isShortMessageEnabled={isShortMessageEnabled} />
                </JobCardContent>
              </JobCard>
            </ArcherElement>
        )}
        </>
      )}
      </JobCardsLayout>
    </ArcherContainer>
    </ScrollableContainer>
  );
};

export default withTranslation(NAMESPACES)(ActionOverviewCard);
